import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RoleSecurityService } from '../authorization/role-security.service';
import { LocationContextService } from '../location-context/location-context.service';
import { IBasePage, Pages } from '../pages.config';
import { EnvService } from './../configuration/env.service';
import { IMenuItem, MainMenuItems, SideMenuItems } from './navigation.model';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    homeRoute = Pages.docking.path; // Default
    anonymousRoute = Pages.login.path;

    constructor(
        private readonly router: Router,
        private readonly locationContextService: LocationContextService,
        private readonly roleSecurityService: RoleSecurityService,
        private readonly env: EnvService
    ) {}

    getPageTitle(): string {
        const page = this.getPageByRoute(this.router.url);
        return page ? page.name : '';
    }

    getHomeRoute(): string {
        return this.homeRoute;
    }

    getAnonymousRoute(): string {
        return this.anonymousRoute;
    }

    getMainMenuItems(): IMenuItem[] {
        return this.filterItems(MainMenuItems);
    }

    getSideMenuItems(): IMenuItem[] {
        return this.filterItems(SideMenuItems);
    }

    isMenuItemAccessible(menuItem: IMenuItem): boolean {
        if (!menuItem.restrictionKey || menuItem.restrictionKey === '') {
            return true;
        }

        return this.roleSecurityService.userCanView(menuItem.restrictionKey);
    }

    filterItems(items: IMenuItem[]): IMenuItem[] {
        // Filter out items not allowed defined by users roles
        items = items.filter(item => this.isMenuItemAccessible(item));

        // Filter out restricted items if location context is not valid
        if (!this.locationContextService.isLocationContextValid()) {
            items = items.filter(item => !item.onlyShowIfLocationContextIsValid);
        } else {
            items = items.filter(item => !item.onlyShowIfLocationContextIsNotValid);
        }

        // Flights filter
        if (!this.env.config.flights.show) {
            items = items.filter(item => item?.text?.toLowerCase() !== Pages?.flights?.name?.toLowerCase());
        }

        return items;
    }

    refresh() {
        window.location.reload();
    }

    // Redirects
    redirectToHome() {
        this.router.navigateByUrl(`/${this.getHomeRoute()}`);
    }

    redirectToAnonymous() {
        this.router.navigateByUrl(`/${this.getAnonymousRoute()}`);
    }

    redirectBack() {
        if (!this.locationContextService.isSingleCenterline()) {
            return this.redirectToCenterlineSelection();
        }

        if (!this.locationContextService.isSingleCenterlineGroup()) {
            return this.redirectToCenterlineGroupSelection();
        }

        if (!this.locationContextService.isSingleTerminal()) {
            return this.redirectToTerminalSelection();
        }
    }

    redirectToLocationContextSelection() {
        return this.redirectToTerminalSelection();
    }

    redirectToTerminalSelection() {
        return this.router.navigateByUrl('/context');
    }

    redirectToCenterlineGroupSelection() {
        return this.router.navigateByUrl(`/context/${Pages.locationContext_CenterlineGroup.path}`);
    }

    redirectToCenterlineSelection() {
        return this.router.navigateByUrl(`/context/${Pages.locationContext_Centerline.path}`);
    }

    redirectToDocking() {
        return this.router.navigateByUrl(`/${Pages.docking.path}`);
    }

    getPageByPath(path: string): IBasePage {
        if (!path) {
            return null;
        }

        let pageFound = null;
        Object.keys(Pages).forEach(key => {
            if (!pageFound) {
                const page = Pages[key] as IBasePage;
                if (page && page.path === path) {
                    pageFound = page;
                }
            }
        });

        return pageFound;
    }

    getPageByRoute(route: string): IBasePage {
        if (!route) {
            return null;
        }

        let pageFound = null;
        Object.keys(Pages).forEach(key => {
            if (!pageFound) {
                const page = Pages[key] as IBasePage;
                if (page && page.router === route) {
                    pageFound = page;
                }
            }
        });

        return pageFound;
    }

    // Location context options count
    setTerminalOptionsCount(count: number) {
        sessionStorage.setItem('TerminalOptionsCount', count.toString());
    }

    getTerminalOptionsCount(): number {
        const countString = sessionStorage.getItem('TerminalOptionsCount');
        return countString ? parseInt(countString) : 0;
    }

    setCenterlineGroupOptionsCount(count: number) {
        sessionStorage.setItem('CenterlineGroupOptionsCount', count.toString());
    }

    getCenterlineGroupOptionsCount(): number {
        const countString = sessionStorage.getItem('CenterlineGroupOptionsCount');
        return countString ? parseInt(countString) : 0;
    }

    setCenterlineOptionsCount(count: number) {
        sessionStorage.setItem('CenterlineOptionsCount', count.toString());
    }

    getCenterlineOptionsCount(): number {
        const countString = sessionStorage.getItem('CenterlineOptionsCount');
        return countString ? parseInt(countString) : 0;
    }

    onMaintenancePage(): boolean {
        const page = this.getPageByRoute(this.router.url);
        return page && page.path === 'maintenance';
    }
}
